import { FunctionComponent, useEffect, useState } from 'react';
import styles from './PreferencesForm.module.scss';
import classNames from 'classnames';
import Input from '../../../../../components/inputs/Input/Input';
import RadioSwitch from '../../../../../components/inputs/RadioSwitch/RadioSwitch';
import CheckboxTagList from '../../../../../components/inputs/CheckboxTagList/CheckboxTagList';
import Checkbox from '../../../../../components/inputs/CheckboxList/_parts/Checkbox/Checkbox';
import MainButton from '../../../../../components/buttons/MainButton/MainButton';
import usePreferencesForm from '../../../hooks/usePreferencesForm';
import { array } from 'prop-types';
import Textarea from '../../../../../components/inputs/Textarea/Textarea';
import InputMessage from '../../../../../components/texts/InputMessage/InputMessage';
import CSSTransitionWrapper from '../../../../../containers/wrappers/CSSTransitionWrapper/CSSTransitionWrapper';
import AstronomicLoader from '../../../../../components/blocks/AstronomicLoader/AstronomicLoader';
import callToast from '../../../../../components/blocks/ToastMessage/_parts/callToast/callToast';

type QuestionType = 'TEXT' | 'PARAGRAPH' | 'NUMBER' | 'CHOICE';
type ChoiceType = 'RADIO' | 'CHECKBOX' | 'SELECT' | 'MULTISELECT' | null;

interface PreferencesFormProps {
  visualType?: 'one' | 'two';
  onSuccessfulSubmit?: () => void;
}

const PreferencesForm: FunctionComponent<PreferencesFormProps> = ({ visualType = 'one', onSuccessfulSubmit }) => {
  const { formSchema, formData, setFormData, validationErrors, loading, error, submitForm, clearFieldError } =
    usePreferencesForm();

  const handleOnSubmit = async (e) => {
    e.preventDefault();

    console.log('Submitting form data:', formData);
    console.log('Form schema:', formSchema);

    const result = await submitForm();

    if (result.success) {
      console.log('Form submitted successfully:', result.data);
      // Handle successful submission (e.g., redirect, show success message)
      if (onSuccessfulSubmit) {
        onSuccessfulSubmit();
      }
    } else {
      console.log('Form validation failed:', result.errors);

      callToast({
        variation: 'warning',
        title: 'Validation Warnings',
        children: 'Please check the fields above for warnings.',
      });
    }
  };

  const handleOnChange = (id, value) => {
    let formattedValue = value;

    if (!Array.isArray(value)) {
      formattedValue = [value];
    }

    // Clear any existing error for this field when user starts typing
    clearFieldError(id);

    setFormData({ ...formData, [id]: formattedValue });
    console.log('formData: ', formData);
  };

  const renderStep = (step) => {
    return step.sections.map((section) => renderSection(section));
  };

  const renderSection = (section) => {
    return (
      <div className={styles['row']} key={section.sectionId}>
        <div className={classNames(styles['row__col'], styles['row__col--1'])}>
          <p className={classNames(styles['title-1'], 'text text--h3')}>{section.sectionName}</p>
          <p className={classNames(styles['text-1'], 'text text--body-2')}>{section.description}</p>
        </div>
        {section.questions.map((question) => renderQuestion(question))}
      </div>
    );
  };

  const renderQuestion = (question) => {
    return (
      <div key={question.questionId} className={classNames(styles['row__col'], styles['row__col--2'])}>
        <div className={styles['question']}>{renderUI(question)}</div>
      </div>
    );
  };

  const renderInputType = (question, fieldErrors, hasError) => {
    return (
      <>
        {question.displayName && (
          <p className={classNames(styles['label-1'], 'text text--body-2 text--bold')}>
            {question.displayName}
            {question.required && <span className={styles['required']}> *</span>}
          </p>
        )}
        {question.label && <p className={classNames(styles['text-2'], 'text text--body-2')}>{question.label}</p>}

        <div className={styles['input']}>
          <Input
            minLength={question.textValidation.minLength}
            maxLength={question.textValidation.maxLength <= 0 ? 9999 : question.textValidation.maxLength}
            placeholder={question.placeholder}
            type={question.type}
            size="large"
            onChange={(e) => handleOnChange(question.questionId, e.target.value)}
            value={formData[question.questionId] ? formData[question.questionId][0] : ''}
            error={hasError}
          />
        </div>

        {hasError && <InputMessage>{fieldErrors[0]}</InputMessage>}
      </>
    );
  };

  const renderUI = (question) => {
    const fieldErrors = validationErrors[question.questionId];
    const hasError = fieldErrors && fieldErrors.length > 0;

    console.log('UI: ', question);
    switch (question.type) {
      case 'TEXT':
        return renderInputType(question, fieldErrors, hasError);
      case 'PARAGRAPH':
        return (
          <>
            {question.displayName && (
              <p className={classNames(styles['label-1'], 'text text--body-2 text--bold')}>
                {question.displayName}
                {question.required && <span className={styles['required']}> *</span>}
              </p>
            )}
            {question.label && <p className={classNames(styles['text-2'], 'text text--body-2')}>{question.label}</p>}

            <div className={styles['input']}>
              <Textarea
                size="large"
                placeholder={question.placeholder}
                onChange={(e) => handleOnChange(question.questionId, e.target.value)}
                value={formData[question.questionId] ? formData[question.questionId][0] : ''}
                error={hasError}
              />
            </div>

            {hasError && <InputMessage>{fieldErrors[0]}</InputMessage>}
          </>
        );

      case 'CHOICE':
        let choiceType = '';

        if (question.choiceType === 'RADIO' || question.choiceType === 'SELECT') {
          choiceType = 'radio';
        } else if (question.choiceType === 'CHECKBOX' || question.choiceType === 'MULTISELECT') {
          choiceType = 'checkbox';
        }

        return (
          <>
            {question.displayName && (
              <p className={classNames(styles['label-1'], 'text text--body-2 text--bold')}>
                {question.displayName}
                {question.required && <span className={styles['required']}> *</span>}
              </p>
            )}
            {question.label && <p className={classNames(styles['text-2'], 'text text--body-2')}>{question.label}</p>}

            <div className={styles['input']}>
              <CheckboxTagList
                mode={choiceType}
                onChange={(selected) => handleOnChange(question.questionId, selected)}
                options={question.options.map((val) => ({ id: val, label: val }))}
                selectedOptions={formData[question.questionId] ? formData[question.questionId] : []}
              />
            </div>

            {hasError && <InputMessage>{fieldErrors[0]}</InputMessage>}
          </>
        );
      default:
        return null;
    }
  };

  // if (loading) {
  //   return <div>Loading form...</div>;
  // }

  // if (error) {
  //   return <div>Error: {error}</div>;
  // }

  return (
    <form className={classNames(styles['form'], styles[`form--visual-type-${visualType}`])}>
      {formSchema?.steps.map((step) => renderStep(step))}

      <MainButton className={styles['submit']} sizeType="large" onClick={handleOnSubmit} disabled={loading}>
        Continue
      </MainButton>

      <CSSTransitionWrapper onEnter={loading} styleVariation="onForeground">
        <AstronomicLoader variation="blurredBackground" logoPosition="top" />
      </CSSTransitionWrapper>
    </form>
  );
};

export default PreferencesForm;
